import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Twilio Verify SMS-OTP modal for customer phone verification.
 *
 * Flow:
 *   - Parent renders <OtpModal open phone onVerified onCancel />
 *   - On open we auto-call /verify/send. Copy the code from the SMS.
 *   - 6-digit input, submit → /verify/check. Success → onVerified().
 *
 * Fallback: if backend returns 501 (Twilio not configured) the modal
 * closes silently and reports verified=true, so the app degrades to
 * trust-based flow without blocking the customer.
 */
const OtpModal = ({ open, phone, onVerified, onCancel }) => {
    const [sending, setSending] = useState(false);
    const [code, setCode] = useState("");
    const [checking, setChecking] = useState(false);
    const [error, setError] = useState("");
    const [cooldown, setCooldown] = useState(0); // secs until resend allowed
    const sentOnce = useRef(false);
    const inputRef = useRef(null);

    const sendCode = async () => {
        setSending(true);
        setError("");
        try {
            await api.post("/verify/send", { phone });
            setCooldown(30);
            // Focus code input right after send.
            setTimeout(() => inputRef.current?.focus(), 50);
        } catch (e) {
            const status = e?.response?.status;
            if (status === 501) {
                // Twilio not configured — degrade gracefully, treat as verified.
                onVerified?.();
                return;
            }
            setError(e?.response?.data?.detail || "Couldn't send the code. Try again.");
        } finally {
            setSending(false);
        }
    };

    // Auto-send on first open per phone.
    useEffect(() => {
        if (!open) {
            sentOnce.current = false;
            setCode("");
            setError("");
            return;
        }
        if (open && !sentOnce.current) {
            sentOnce.current = true;
            sendCode();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, phone]);

    // Cooldown ticker.
    useEffect(() => {
        if (cooldown <= 0) return;
        const t = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
        return () => clearInterval(t);
    }, [cooldown]);

    const submit = async () => {
        if (code.length < 4) return;
        setChecking(true);
        setError("");
        try {
            const r = await api.post("/verify/check", { phone, code });
            if (r.data?.verified) {
                toast.success("Phone verified");
                onVerified?.();
            } else {
                setError("That code didn't match. Check the SMS and try again.");
                setCode("");
            }
        } catch (e) {
            setError(e?.response?.data?.detail || "Verification failed. Try again.");
        } finally {
            setChecking(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    key="otp-backdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[70] bg-[#2C1A12]/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
                    data-testid="otp-modal"
                >
                    <motion.div
                        initial={{ y: 40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 40, opacity: 0 }}
                        transition={{ type: "spring", stiffness: 220, damping: 24 }}
                        className="w-full max-w-sm rounded-3xl bg-white p-7 text-center shadow-[0_30px_60px_rgba(44,26,18,0.35)]"
                    >
                        <div className="mx-auto inline-flex h-14 w-14 rounded-full bg-[#FF4F18] items-center justify-center text-white">
                            <ShieldCheck className="h-6 w-6" strokeWidth={2.5} />
                        </div>
                        <h2 className="mt-4 font-display font-black text-2xl">
                            Verify your number
                        </h2>
                        <p className="mt-1.5 text-sm text-[#8C7A72]">
                            We texted a 6-digit code to
                            <span className="font-bold text-[#2C1A12]"> {phone}</span>.
                        </p>

                        <input
                            ref={inputRef}
                            data-testid="otp-code-input"
                            type="tel"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            maxLength={6}
                            placeholder="••••••"
                            value={code}
                            onChange={(e) => {
                                setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                                setError("");
                            }}
                            onKeyDown={(e) => e.key === "Enter" && submit()}
                            className="mt-6 w-full text-center text-3xl font-display font-black tracking-[0.4em] py-4 px-5 rounded-2xl bg-[#F9F6F0] border-2 border-[#E8DCCB] focus:border-[#FF4F18] outline-none transition-colors"
                        />

                        {error && (
                            <div
                                data-testid="otp-error"
                                className="mt-3 text-xs text-[#B4361E] font-semibold"
                            >
                                {error}
                            </div>
                        )}

                        <button
                            onClick={submit}
                            disabled={code.length < 4 || checking}
                            data-testid="otp-submit"
                            className={`mt-5 w-full py-4 rounded-full font-display font-bold text-base flex items-center justify-center gap-2 ${
                                code.length >= 4 && !checking
                                    ? "bg-[#FF4F18] text-white active:scale-[0.98]"
                                    : "bg-[#E8DCCB] text-[#8C7A72]"
                            }`}
                        >
                            {checking ? "Checking…" : "Verify & continue"}
                        </button>

                        <div className="mt-4 flex items-center justify-between text-xs">
                            <button
                                onClick={onCancel}
                                data-testid="otp-cancel"
                                className="text-[#8C7A72] hover:text-[#2C1A12] font-semibold"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={sendCode}
                                disabled={cooldown > 0 || sending}
                                data-testid="otp-resend"
                                className="text-[#FF4F18] font-bold disabled:text-[#8C7A72] disabled:opacity-60 flex items-center gap-1.5"
                            >
                                <MessageSquare className="h-3 w-3" strokeWidth={2.5} />
                                {cooldown > 0 ? `Resend in ${cooldown}s` : sending ? "Sending…" : "Resend code"}
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default OtpModal;
